// The account-request e-mails the Overwolf pages ask the user to send.
//
// Neither cancelling a subscription nor changing a plan has an endpoint yet, so
// the request travels by e-mail to the team. That is not a placeholder for a
// button that silently does nothing - it is the actual mechanism, and the pages
// say so plainly: here is the address, here is the subject, here is a button
// that opens your mail app with all of it filled in.
//
// The subject line is the part the team reads first, so its shape is fixed:
//
//     <the user's login e-mail> unsubscribe
//     <the user's login e-mail> change plan
//
// Keep the account address at the START of the subject. It is what makes the
// request findable and matchable to an account without opening the message.

export const SUPPORT_EMAIL = 'songhunt@mypart.com';

export interface AccountRequestEmail {
  to: string;
  subject: string;
  body: string;
  /** Ready to use as an href, so the browser hands off to the mail client. */
  mailto: string;
  /** Everything at once, for the copy-to-clipboard fallback. */
  plainText: string;
}

function build(subject: string, lines: string[]): AccountRequestEmail {
  const body = lines.join('\n');
  return {
    to: SUPPORT_EMAIL,
    subject,
    body,
    // encodeURIComponent turns the newlines into %0A, which every mail client
    // expands back into real line breaks.
    mailto: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
    plainText: `To: ${SUPPORT_EMAIL}\nSubject: ${subject}\n\n${body}`,
  };
}

/** "Please cancel my subscription", from the /ow-cancel page. */
export function buildUnsubscribeEmail(account: {
  email: string;
  userId?: string;
}): AccountRequestEmail {
  return build(`${account.email} unsubscribe`, [
    'Hi Songhunt team,',
    '',
    'Please cancel my Overwolf subscription.',
    '',
    `Account: ${account.email}`,
    ...(account.userId ? [`User ID: ${account.userId}`] : []),
    '',
    'Sent from the Songhunt page for Overwolf.',
  ]);
}

/** "Please move me to another plan", from the /ow-plan page. */
export function buildPlanChangeEmail(account: {
  email: string;
  /** Omitted when the page could not establish which plan they are on. */
  fromPlan?: string;
  toPlan: string;
  /** From the signed-in account, when the page managed to sign in. */
  userId?: string;
}): AccountRequestEmail {
  return build(`${account.email} change plan`, [
    'Hi Songhunt team,',
    '',
    `Please change my Overwolf plan to ${account.toPlan}.`,
    '',
    `Account: ${account.email}`,
    ...(account.userId ? [`User ID: ${account.userId}`] : []),
    ...(account.fromPlan ? [`Current plan: ${account.fromPlan}`] : []),
    `Requested plan: ${account.toPlan}`,
    '',
    'Sent from the Songhunt page for Overwolf.',
  ]);
}
